import { useEffect, useState, useRef } from 'react';
import api from '../api';
import { Download, Mail, FileText, Upload, CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react';

/**
 * Clinical Affiliation Banner — shown on every medical/hybrid online course.
 *
 * • LPN requires 600 supervised clinical hours (gates the cert).
 * • CNA, Phlebotomy, EMT, Medical Aide, Medical Terminology require a
 *   minimum of 20 hours each.
 *
 * The student downloads the Clinical Affiliation / Student Intern Form,
 * gets it signed at the site of their choice, uploads it here, and admin
 * verifies. The same admin-verify pipeline is reused per program.
 */
const PROGRAM_HOURS = {
  'lpn-prep': 600,
  'cna-prep': 20,
  'phlebotomy-prep': 20,
  'emt-prep': 20,
  'medical-aide-prep': 20,
  'medical-terminology-prep': 20,
};

const PROGRAM_LABELS = {
  'lpn-prep': 'PN Tech (Phase 1 of 12)',
  'cna-prep': 'CMA',
  'phlebotomy-prep': 'Phlebotomy Technician',
  'emt-prep': 'EMT',
  'medical-aide-prep': 'Medical Aide',
  'medical-terminology-prep': 'Medical Terminology',
};

const ClinicalAffiliationBanner = ({ user, apiBaseUrl, courseId = 'lpn-prep' }) => {
  const [statusInfo, setStatusInfo] = useState({ status: 'not_submitted', upload: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const fileRef = useRef(null);

  const requiredHours = PROGRAM_HOURS[courseId] ?? 20;
  const programLabel = PROGRAM_LABELS[courseId] ?? 'Apprenticeship';
  const isLpn = courseId === 'lpn-prep';

  const refresh = async () => {
    try {
      const r = await api.get(`/clinical-affiliation-form/${user.id}/status`, {
        params: { program: courseId },
      });
      setStatusInfo(r.data);
    } catch (_) { /* keep prior state */ }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [user?.id, courseId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setInfo(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name || 'affiliation.pdf');
      await api.post(`/clinical-affiliation-form/${user.id}/upload`, fd, {
        params: { program: courseId },
      });
      setInfo('Uploaded — admin will verify shortly.');
      await refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const status = statusInfo.status;
  const upload = statusInfo.upload;
  const statusMeta = STATUS_META[status] || STATUS_META.not_submitted;

  return (
    <div
      data-testid="clinical-affiliation-banner"
      style={{
        maxWidth: 1400,
        margin: '16px auto 0',
        padding: '20px 24px',
        background: 'linear-gradient(135deg, #0b2545 0%, #13315c 100%)',
        borderRadius: 12,
        border: '2px solid #c9a227',
        color: '#f8fafc',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <FileText size={20} color="#f7d774" />
        <strong style={{ letterSpacing: 0.6, color: '#f7d774', textTransform: 'uppercase', fontSize: '0.78rem' }}>
          {programLabel} · Required Clinical Hours: {requiredHours} (minimum)
        </strong>
        <span
          data-testid="affiliation-status-pill"
          style={{
            marginLeft: 'auto',
            background: statusMeta.bg,
            color: statusMeta.fg,
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: '0.72rem',
            fontWeight: 800,
            letterSpacing: 0.4,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {statusMeta.icon} {statusMeta.label}
        </span>
      </div>
      <p style={{ margin: '0 0 8px', fontSize: '0.9rem', lineHeight: 1.55 }}>
        Your {programLabel} program combines online reading + quizzes (here) with{' '}
        <strong>{requiredHours} supervised clinical hours</strong> at a hospital,
        long-term-care facility, lab, urgent care, or clinic of <em>your choice</em>.
        Download the Clinical Affiliation / Student Intern Form, take it to your selected
        site, have the supervising RN/LPN/MD complete and sign it, then upload the signed
        PDF here.
        {isLpn
          ? ' Your Certificate of Completion is released after admin verifies the form.'
          : ' Admin will verify your signed form on file.'}
      </p>
      <p style={{ margin: '0 0 12px', fontSize: '0.78rem', color: '#fde68a', fontStyle: 'italic' }}>
        Additional requirements may apply (immunizations, background check, BLS/CPR, etc.).
        Additional charges may apply and are the Student's responsibility.
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <a
          href={`${apiBaseUrl}/api/clinical-affiliation-form/${user.id}?program=${courseId}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-affiliation-form-btn"
          style={primaryBtn}
        >
          <Download size={16} /> Download Form (PDF)
        </a>

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="upload-affiliation-form-btn"
          disabled={busy}
          style={{ ...primaryBtn, background: busy ? '#94a3b8' : '#22c55e', color: '#fff', border: 'none', cursor: busy ? 'not-allowed' : 'pointer' }}
        >
          {busy ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={16} />}
          {upload ? 'Re-upload Signed Form' : 'Upload Signed Form (PDF)'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={onUpload}
          data-testid="affiliation-file-input"
          style={{ display: 'none' }}
        />

        <a
          href="mailto:medcare.nursing@yahoo.com?subject=Clinical%20Affiliation%20Form%20Submission"
          data-testid="email-admin-affiliation-link"
          style={secondaryBtn}
        >
          <Mail size={14} /> Email Admin
        </a>
      </div>

      {info && <div style={infoStyle('#dcfce7', '#065f46')} data-testid="affiliation-info">{info}</div>}
      {error && <div style={infoStyle('#fee2e2', '#991b1b')} data-testid="affiliation-error">{error}</div>}

      {upload && (
        <div data-testid="affiliation-upload-meta" style={{
          marginTop: 10,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(247,215,116,0.35)',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: '0.8rem',
          color: '#e2e8f0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 14,
        }}>
          <span><strong>Last upload:</strong> {upload.original_filename}</span>
          <span>· {!upload.size ? '—' : upload.size < 1024 ? '<1 KB' : `${Math.round(upload.size / 1024)} KB`}</span>
          <span>· {new Date(upload.uploaded_at).toLocaleString()}</span>
          {upload.verified_by && <span>· Reviewed by {upload.verified_by}</span>}
          {upload.rejection_note && <span style={{ color: '#fecaca' }}>· Reason: {upload.rejection_note}</span>}
        </div>
      )}
    </div>
  );
};

const STATUS_META = {
  not_submitted: { label: 'Not Submitted', bg: '#fef3c7', fg: '#92400e', icon: <Clock size={12} /> },
  pending:       { label: 'Pending Verification', bg: '#dbeafe', fg: '#1e3a8a', icon: <Clock size={12} /> },
  verified:      { label: 'Verified', bg: '#dcfce7', fg: '#065f46', icon: <CheckCircle2 size={12} /> },
  rejected:      { label: 'Rejected — Re-upload', bg: '#fee2e2', fg: '#991b1b', icon: <XCircle size={12} /> },
};

const primaryBtn = {
  background: '#f7d774',
  color: '#0b2545',
  padding: '10px 18px',
  borderRadius: 10,
  fontWeight: 800,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: '0.9rem',
  border: 'none',
};
const secondaryBtn = {
  background: 'rgba(255,255,255,0.12)',
  color: '#fff',
  padding: '10px 18px',
  borderRadius: 10,
  fontWeight: 600,
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: '0.9rem',
  border: '1px solid rgba(247,215,116,0.55)',
};
const infoStyle = (bg, fg) => ({
  marginTop: 8,
  padding: '8px 12px',
  borderRadius: 8,
  background: bg,
  color: fg,
  fontSize: '0.8rem',
  fontWeight: 600,
});

export default ClinicalAffiliationBanner;
